import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/payment')({
  component: PaymentPage,
})

type Plan = 'basic' | 'premium'

interface PlanSpec {
  id: Plan
  badge?: string
  name: string
  price: string
  tagline: string
  features: string[]
  ctaText: string
  highlighted?: boolean
}

const PLANS: PlanSpec[] = [
  {
    id: 'basic',
    name: '베이직',
    price: '월 19,000원',
    tagline: '첫 프로젝트 + 친구들 작품 둘러보기',
    features: [
      'AI와 무제한으로 대화',
      '한 달에 프로젝트 3개까지 만들 수 있어요',
      '하루 대화량 넉넉히 (작품 하나 만들기에 충분한 양)',
      '친구들 작품에 내 작품 5개까지 공개',
      '시작 템플릿 35개 사용 가능',
    ],
    ctaText: '베이직으로 시작하기',
  },
  {
    id: 'premium',
    badge: '추천',
    name: '프리미엄',
    price: '월 39,000원',
    tagline: '졸업까지 함께 — 무제한 만들기, 막힘없는 AI, 졸업 후 가이드까지',
    features: [
      '어려운 작업일수록 더 똑똑한 AI가 자동으로 도와줘요',
      '프로젝트 무제한으로 만들고 공개',
      '내 작품 파일 50GB까지 저장',
      '외부 데이터 가져오기 무제한',
      '졸업할 때 내 도메인으로 옮기는 안내까지',
    ],
    ctaText: '프리미엄으로 시작하기',
    highlighted: true,
  },
]

function PaymentPage() {
  const [plan, setPlan] = useState<Plan>('premium')
  const [groupCode, setGroupCode] = useState('')

  function handleSubmit() {
    toast.info(
      '지금은 베타 무료 기간이에요. 결제 연결은 곧 토스페이먼츠와 카카오페이로 열어둘게요.',
    )
  }

  return (
    <div className="bg-bg min-h-[calc(100vh-56px)]">
      <div className="mx-auto max-w-[1080px] px-6 py-16">
        <header className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-soft px-3 py-1 text-[12px] font-bold tracking-tight text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            첫 프로젝트는 결제 없이 무료
          </span>
          <h1 className="kr-tight mt-5 text-[28px] font-extrabold tracking-[-0.03em] sm:text-[36px]">
            바이브타운, 같이 시작해요
          </h1>
          <p className="mt-3 text-[15px] text-text-2">
            첫 프로젝트는 무료예요. 만들어보고 마음에 들면 그때 결정해도 돼요.
          </p>
        </header>

        <section className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
          {PLANS.map((p) => {
            const selected = plan === p.id
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setPlan(p.id)}
                className={`flex flex-col gap-4 rounded-2xl border-2 bg-white p-7 text-left transition-all ${
                  selected ? 'border-primary shadow-md' : 'border-border hover:border-border-strong'
                }`}
              >
                <div className="flex items-baseline gap-2">
                  <h2 className="kr-tight text-[22px] font-extrabold tracking-tight">{p.name}</h2>
                  {p.badge && (
                    <span
                      className="rounded-md bg-accent-soft px-2 py-0.5 text-[11px] font-bold"
                      style={{ color: 'var(--color-accent-deep)' }}
                    >
                      {p.badge}
                    </span>
                  )}
                </div>
                <div>
                  <div className="text-[26px] font-extrabold tracking-tight text-text">{p.price}</div>
                  <div className="text-[13px] text-text-3">학생 1인 기준</div>
                </div>
                <p className="text-[14px] text-text-2">{p.tagline}</p>
                <ul className="flex flex-col gap-2 text-[13px] text-text">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="mt-0.5 text-primary">✓</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex items-center gap-2 text-[12px] font-semibold">
                  <span
                    className={`grid h-4 w-4 place-items-center rounded-full border-2 ${
                      selected ? 'border-primary' : 'border-border-strong'
                    }`}
                  >
                    {selected && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                  </span>
                  <span className={selected ? 'text-primary' : 'text-text-2'}>
                    {selected ? '선택됨' : '이 플랜으로 변경'}
                  </span>
                </div>
              </button>
            )
          })}
        </section>

        <section className="mt-8 rounded-2xl bg-white p-7 shadow-sm">
          <h3 className="text-[16px] font-bold tracking-tight">결제 수단</h3>
          <p className="mt-1 text-[13px] text-text-3">
            지금은 베타 무료 기간이에요. 곧 토스페이먼츠와 카카오페이로 결제할 수 있어요.
          </p>

          <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="rounded-xl border border-border-strong bg-surface p-4">
              <div className="flex items-center gap-2 text-[13px] font-bold">
                <span className="grid h-6 w-6 place-items-center rounded-md bg-primary text-[11px] font-extrabold text-white">
                  T
                </span>
                토스페이먼츠
              </div>
              <p className="mt-1 text-[11px] text-text-3">카드 · 계좌이체 · 휴대폰</p>
            </div>
            <div className="rounded-xl border border-border-strong bg-surface p-4">
              <div className="flex items-center gap-2 text-[13px] font-bold">
                <span
                  className="grid h-6 w-6 place-items-center rounded-md bg-[#fee500] text-[11px] font-extrabold"
                  style={{ color: '#191919' }}
                >
                  K
                </span>
                카카오페이
              </div>
              <p className="mt-1 text-[11px] text-text-3">간편결제</p>
            </div>
          </div>

          <div className="mt-6 border-t border-border pt-5">
            <label className="text-[12px] font-semibold text-text-2">학원·학교 단체 가입 코드</label>
            <input
              type="text"
              value={groupCode}
              onChange={(e) => setGroupCode(e.target.value.toUpperCase())}
              maxLength={14}
              placeholder="VIBE-XXXX-XXXX"
              className="mt-2 w-full rounded-xl border border-border-strong bg-white px-4 py-3 font-mono text-[14px] tracking-[0.1em] outline-none focus:border-primary focus:shadow-[0_0_0_4px_rgba(49,130,246,0.12)]"
            />
            <p className="mt-1 text-[11px] text-text-3">
              학원에서 받은 코드가 있으면 입력해 주세요. 단체 할인이 자동으로 적용돼요.
            </p>
          </div>

          <button
            onClick={handleSubmit}
            className="mt-6 w-full rounded-xl bg-primary py-4 text-[15px] font-bold text-white hover:bg-primary-hover"
          >
            {PLANS.find((p) => p.id === plan)?.ctaText ?? '결제 진행'}
          </button>
          <p className="mt-3 text-center text-[11px] text-text-3">
            이어서 진행하면{' '}
            <Link to="/" className="font-semibold text-primary">
              이용약관과 개인정보 처리방침
            </Link>
            에 동의한 것으로 봐요.
          </p>
        </section>
      </div>
    </div>
  )
}
